import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, Mail, Play, Pause, Lock, Settings, Eye, Trash2, MapPin, AlertTriangle, Loader2, RefreshCw, X, Edit2 } from 'lucide-react';
import { crmMailingListsApi, CrmMailingList } from '../services/crmApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function MailingLists() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [lists, setLists] = useState<CrmMailingList[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState<string | null>(null);
  const [intakeMessage, setIntakeMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form state for create
  const [formData, setFormData] = useState({
    Name: '',
    Description: '',
    Status: 'open',
    Intake_Limit: ''
  });
  // Derive team from workspace scope
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const fetchLists = async () => {
    setLoading(true);
    try {
      const data = await crmMailingListsApi.list();
      setLists(data);
    } catch (err) {
      console.error('Failed to fetch lists:', err);
      toast.error('Failed to load mailing lists');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchLists();
  }, []);
  // Filter by team and region scope
  const filteredLists = lists.filter(l => {
    // Team filter: only show lists belonging to current workspace or with no team
    if (l.Team && l.Team !== currentWorkspace.name && l.Team !== team) return false;
    // Region filter
    if (currentSubRegion) {
      return !l.Region || l.Region === currentSubRegion;
    }
    return true;
  });
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.Name.trim()) return;
    setIsSubmitting(true);
    try {
      const payload: Partial<CrmMailingList> = {
        Name: formData.Name,
        Description: formData.Description,
        Status: formData.Status,
        Intake_Limit: formData.Intake_Limit ? parseInt(formData.Intake_Limit) : undefined,
        Team: team
      };
      if (currentSubRegion) {
        payload.Region = currentSubRegion;
      }
      await crmMailingListsApi.create(payload);
      toast.success('Mailing list created successfully');
      setShowCreateModal(false);
      setFormData({
        Name: '',
        Description: '',
        Status: 'open',
        Intake_Limit: ''
      });
      fetchLists();
    } catch (err) {
      console.error('Failed to create list:', err);
      toast.error('Failed to create mailing list');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (id: string) => {
    try {
      await crmMailingListsApi.delete(id);
      toast.success('Mailing list deleted');
      setDeleteConfirm(null);
      fetchLists();
    } catch (err) {
      console.error('Failed to delete list:', err);
      toast.error('Failed to delete mailing list');
    }
  };
  const handleStatusChange = async (id: string, newStatus: string, message?: string) => {
    try {
      const payload: Partial<CrmMailingList> = {
        Status: newStatus
      };
      if (newStatus === 'paused' && message) payload.Paused_Message = message;
      if (newStatus === 'closed' && message) payload.Closed_Message = message;
      await crmMailingListsApi.update(id, payload);
      toast.success(`List status updated to ${newStatus}`);
      fetchLists();
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update list status');
    }
  };
  const openIntakeModal = (id: string, status: 'paused' | 'closed') => {
    setShowIntakeModal(id);
    // Store intended status in a way we can retrieve it, or just handle in the modal logic
    // For simplicity, we'll assume the modal knows which status to apply based on button click
  };
  const getIntakeStatusConfig = (status: string) => {
    switch (status) {
      case 'open':
        return {
          icon: Play,
          color: 'text-green-600',
          bg: 'bg-green-50',
          border: 'border-green-200',
          label: 'Accepting Signups'
        };
      case 'paused':
        return {
          icon: Pause,
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          label: 'Paused'
        };
      case 'closed':
        return {
          icon: Lock,
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200',
          label: 'Closed'
        };
      default:
        return {
          icon: Play,
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          label: 'Unknown'
        };
    }
  };
  // Stats calculation
  const totalSubscribers = filteredLists.reduce((sum, list) => sum + (list.Subscriber_Count || 0), 0);
  const openLists = filteredLists.filter(l => l.Status === 'open').length;
  const pausedLists = filteredLists.filter(l => l.Status === 'paused').length;
  return <div className="space-y-6" data-id="element-5027" data-mp="x1x0gg99k61m">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5028" data-mp="1kp4ug218rsoc">
        <div className="min-w-0" data-id="element-5029" data-mp="1or04pw15hge9">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5030" data-mp="1opyhyuarv5gp">
            Mailing Lists
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5031" data-mp="1az1z3v6ne2mu">
            Manage your audience lists, intake controls, and subscribers (synced
            with Zoho CRM).
            {currentSubRegion && <span className="ml-1 font-medium text-blue-600" data-id="element-5032" data-mp="23mxwl241jyko">
                Filtered by: {currentSubRegion}
              </span>}
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-5033" data-mp="2ibhg8g1qpzu3">
          <button onClick={fetchLists} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-5034" data-mp="2i94zg67pxvim">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5035" data-mp="28bgw4rw1rfou" />
          </button>
          <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-5036" data-mp="23dfe8aacxmey">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-5037" data-mp="1pm0qxr9f2z23" />
            <span data-id="element-5038" data-mp="1pmtpi4y82i95">Create List</span>
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5039" data-mp="1klcsikiuzfxu">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5040" data-mp="1fn2u811u7z80">
          <div className="flex items-center gap-3" data-id="element-5041" data-mp="1bm989d7yguhr">
            <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-5042" data-mp="1bnkinvvwp0hu">
              <Mail className="h-5 w-5 text-blue-600" data-id="element-5043" data-mp="3n9cokicy3d6e" />
            </div>
            <div className="min-w-0" data-id="element-5044" data-mp="3rb6lcfk0rx1a">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5045" data-mp="3ra505i9ahu5u">
                {filteredLists.length}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-5046" data-mp="3lqw7j3qc9n">Total Lists</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5047" data-mp="33mf0yaco8iz2">
          <div className="flex items-center gap-3" data-id="element-5048" data-mp="33k2bzd8hx8ci">
            <div className="p-2 bg-green-50 rounded-lg flex-shrink-0" data-id="element-5049" data-mp="38m9s8f0czakn">
              <Play className="h-5 w-5 text-green-600" data-id="element-5050" data-mp="3dkl2gqukfn3e" />
            </div>
            <div className="min-w-0" data-id="element-5051" data-mp="3di8dhtqe4cgu">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5052" data-mp="3ibjh7lzwod4j">
                {openLists}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-5053" data-mp="2kum8qi5ff1se">
                Accepting Signups
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5054" data-mp="1j7eku3u44rrc">
          <div className="flex items-center gap-3" data-id="element-5055" data-mp="1j7obzuqw94ym">
            <div className="p-2 bg-yellow-50 rounded-lg flex-shrink-0" data-id="element-5056" data-mp="1e93bdu7qcj9n">
              <Pause className="h-5 w-5 text-yellow-600" data-id="element-5057" data-mp="1s831l6aw567x" />
            </div>
            <div className="min-w-0" data-id="element-5058" data-mp="1s5zzhnwgrmmu">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5059" data-mp="1n8i12rg2opt3">
                {pausedLists}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-5060" data-mp="225h3m0w2hsgp">Paused</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5061" data-mp="1x6e8p99sx2mg">
          <div className="flex items-center gap-3" data-id="element-5062" data-mp="2az3zrs46sxqf">
            <div className="p-2 bg-purple-50 rounded-lg flex-shrink-0" data-id="element-5063" data-mp="2b0oqt7ralyjj">
              <Users className="h-5 w-5 text-purple-600" data-id="element-5064" data-mp="26ya7ynnmiys9" />
            </div>
            <div className="min-w-0" data-id="element-5065" data-mp="2kvyv6255yi0h">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5066" data-mp="2kx1kw6qr2eet">
                {totalSubscribers.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-5067" data-mp="2zjqgvylsm9gg">
                Total Subscribers
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lists Grid */}
      {loading && filteredLists.length === 0 ? <div className="p-12 text-center bg-white border border-gray-200 rounded-lg" data-id="element-5068" data-mp="2pkhtxppchv3l">
          <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-5069" data-mp="2ujcm3i97cw0d" />
          <p className="mt-2 text-sm text-gray-500" data-id="element-5070" data-mp="2gsfu9tn3oiud">Loading mailing lists...</p>
        </div> : filteredLists.length === 0 ? <div className="p-12 text-center bg-white border border-gray-200 rounded-lg" data-id="element-5071" data-mp="2lkhafqne2tpo">
          <Mail className="mx-auto h-12 w-12 text-gray-400" data-id="element-5072" data-mp="26vumj0jp4tmu" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5073" data-mp="2btw8bt8ib720">
            No mailing lists found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5074" data-mp="1xtlgonzdhofs">
            {currentSubRegion ? `No lists found for region "${currentSubRegion}".` : 'Create a list to start collecting subscribers.'}
          </p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-5075" data-mp="2sgp1db67hozu">
          {filteredLists.map(list => {
        const intakeConfig = getIntakeStatusConfig(list.Status);
        const IntakeIcon = intakeConfig.icon;
        const subscriberCount = list.Subscriber_Count || 0;
        const intakeLimit = list.Intake_Limit;
        const isNearLimit = intakeLimit && subscriberCount >= intakeLimit * 0.9;
        return <div key={list.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col" data-id="element-5076" data-mp="3g2vjwrd9an4b">
                {/* Header */}
                <div className={`px-4 py-3 ${intakeConfig.bg} border-b ${intakeConfig.border} flex-shrink-0`} data-id="element-5077" data-mp="36d2ek83pj48i">
                  <div className="flex items-center justify-between gap-2" data-id="element-5078" data-mp="2pa9jcypzfgf">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-5079" data-mp="2reocpy5pyvh">
                      <IntakeIcon className={`h-4 w-4 ${intakeConfig.color} flex-shrink-0`} data-id="element-5080" data-mp="1nee9rfin4lqg" />
                      <span className={`text-sm font-medium ${intakeConfig.color} truncate`} data-id="element-5081" data-mp="1ierw54ywh3h4">
                        {intakeConfig.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" data-id="element-5082" data-mp="14g1sf6hks5js">
                      {list.Status !== 'open' && <button onClick={() => handleStatusChange(list.id, 'open')} className="p-1 hover:bg-white/50 rounded text-green-600" title="Open Intake" data-id="element-5083" data-mp="zjbdwnlf5i0h">
                          <Play className="h-4 w-4" data-id="element-5084" data-mp="2ptejvfi8lqfy" />
                        </button>}
                      {list.Status !== 'paused' && <button onClick={() => {
                  setShowIntakeModal(list.id);
                  // We'll handle the actual status change in the modal
                }} className="p-1 hover:bg-white/50 rounded text-yellow-600" title="Pause Intake" data-id="element-5085" data-mp="2ryb7vvvhxa3r">
                          <Pause className="h-4 w-4" data-id="element-5086" data-mp="297tia05ymumm" />
                        </button>}
                      {list.Status !== 'closed' && <button onClick={() => {
                  setShowIntakeModal(list.id);
                  // We'll handle the actual status change in the modal
                }} className="p-1 hover:bg-white/50 rounded text-red-600" title="Close Intake" data-id="element-5087" data-mp="2e7ejyicilh31">
                          <Lock className="h-4 w-4" data-id="element-5088" data-mp="1veu3y1hwz6gn" />
                        </button>}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4 flex-1" data-id="element-5089" data-mp="8kgut9fl5mwd">
                  <div className="min-w-0" data-id="element-5090" data-mp="3jt1gh3vwtyz">
                    <h3 className="text-lg font-medium text-gray-900 truncate" data-id="element-5091" data-mp="3mp0k8rgxop0">
                      {list.Name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2" data-id="element-5092" data-mp="3psc5x5xb1kfs">
                      {list.Description || 'No description'}
                    </p>
                  </div>

                  {/* Subscriber Count */}
                  <div className="space-y-2" data-id="element-5093" data-mp="3h0a8lgij8k9q">
                    <div className="flex items-center justify-between text-sm" data-id="element-5094" data-mp="3gx4lu7xdluh6">
                      <span className="text-gray-600" data-id="element-5095" data-mp="3byb3o2759fao">Subscribers</span>
                      <span className="font-semibold text-gray-900" data-id="element-5096" data-mp="36zzuzusl5au1">
                        {subscriberCount.toLocaleString()}
                      </span>
                    </div>
                    {intakeLimit && <>
                        <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-5097" data-mp="1ay6i9a0glmpw">
                          <div className={`h-2 rounded-full ${isNearLimit ? 'bg-yellow-500' : 'bg-blue-500'}`} style={{
                    width: `${Math.min(subscriberCount / intakeLimit * 100, 100)}%`
                  }} data-id="element-5098" data-mp="1b095xne533rg" />
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-5099" data-mp="s8q3bho11rpy">
                          <span data-id="element-5100" data-mp="dbh98g7ca9uj">Limit: {intakeLimit.toLocaleString()}</span>
                          {isNearLimit && <span className="flex items-center gap-1 text-yellow-600" data-id="element-5101" data-mp="ia0usfi0tjmd">
                              <AlertTriangle className="h-3 w-3 flex-shrink-0" data-id="element-5102" data-mp="27pqkkrv3o05a" />
                              <span className="whitespace-nowrap" data-id="element-5103" data-mp="2cns1b651zndl">
                                Near capacity
                              </span>
                            </span>}
                        </div>
                      </>}
                  </div>

                  {/* Meta Badges */}
                  <div className="flex flex-wrap gap-2 text-xs" data-id="element-5104" data-mp="as2rwtuch6s4">
                    {list.Region && <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded whitespace-nowrap" data-id="element-5105" data-mp="5xgcmfl56u2r">
                        <MapPin className="h-3 w-3 flex-shrink-0" data-id="element-5106" data-mp="fpui9jfccku7" />
                        {list.Region}
                      </span>}
                    {list.Team && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded whitespace-nowrap" data-id="element-5107" data-mp="op994jzf6xf8">
                        <Users className="h-3 w-3 flex-shrink-0" data-id="element-5108" data-mp="2vh0bxy99yyvl" />
                        {list.Team}
                      </span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-shrink-0" data-id="element-5109" data-mp="ug2atflnl6gj">
                  <Link to={`/mailing-lists/${list.id}`} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 whitespace-nowrap" data-id="element-5110" data-mp="13exmh77gv8r8">
                    <Eye className="h-4 w-4 flex-shrink-0" data-id="element-5111" data-mp="1dbsjflo3d13e" />
                    <span data-id="element-5112" data-mp="1ibyaazmjejkf">View Details</span>
                  </Link>
                  <div className="flex items-center gap-2" data-id="element-5113" data-mp="1ia3ntgvmaje8">
                    <button onClick={() => setDeleteConfirm(list.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-5114" data-mp="1n3ptuq79zp12">
                      <Trash2 className="h-4 w-4" data-id="element-5115" data-mp="1w18mafn915mz" />
                    </button>
                  </div>
                </div>
              </div>;
      })}
        </div>}

      {/* Create Modal */}
      {showCreateModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5116" data-mp="2670bdocy0mpa">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5117" data-mp="269mgzig9ws4v">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} data-id="element-5118" data-mp="218gmpg6yswt8" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-5119" data-mp="2tx43aygrqy0g">
              <div className="flex items-center justify-between mb-4" data-id="element-5120" data-mp="2ox9ed0gk0ii1">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5121" data-mp="2p03zyvbb09d1">
                  Create Mailing List
                </h3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-5122" data-mp="2g5zbujnk1c17">
                  <X className="h-5 w-5" data-id="element-5123" data-mp="3cp6pepc6j6i3" />
                </button>
              </div>
              <form onSubmit={handleCreate} className="space-y-4" data-id="element-5124" data-mp="37vc5bksnwpih">
                <div data-id="element-5125" data-mp="3rjmiyev641wb">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5126" data-mp="3rjcsyra4huqm">
                    List Name
                  </label>
                  <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Name} onChange={e => setFormData({
                ...formData,
                Name: e.target.value
              })} data-id="element-5127" data-mp="3hmq9a5tj99q6" />
                </div>
                <div data-id="element-5128" data-mp="96er2etrsrtt">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5129" data-mp="95n3nrabu0em">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Description} onChange={e => setFormData({
                ...formData,
                Description: e.target.value
              })} data-id="element-5130" data-mp="2ozx7dk4eq4el" />
                </div>
                <div data-id="element-5131" data-mp="1gz59boay6yp9">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5132" data-mp="t8sq3ha95fjd">
                    Intake Limit (Optional)
                  </label>
                  <input type="number" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Intake_Limit} onChange={e => setFormData({
                ...formData,
                Intake_Limit: e.target.value
              })} data-id="element-5133" data-mp="oazicjtawdgz" />
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-5134" data-mp="2vr83kw4aruu3">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5135" data-mp="2rp3d2695bebo">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting || !formData.Name.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-id="element-5136" data-mp="3fd31v0mwcunm">
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5137" data-mp="1u1309cmkytp7" />}
                    Create List
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      {/* Intake Message Modal */}
      {showIntakeModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5138" data-mp="9j0tlataq6y6">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5139" data-mp="9iin7lcx6ccj">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowIntakeModal(null)} data-id="element-5140" data-mp="3rvylqvjujvhs" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-5141" data-mp="3hyu0cxxts421">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-5142" data-mp="3i16sg3xarust">
                Configure Intake Message
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-5143" data-mp="38ynpzkxfbk73">
                This message will be shown to users when they try to sign up
                while the list is paused or closed.
              </p>
              <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="e.g., We're currently at capacity. Please check back soon!" value={intakeMessage} onChange={e => setIntakeMessage(e.target.value)} data-id="element-5144" data-mp="176982luvqfqo" />
              <div className="flex justify-end gap-3 mt-4" data-id="element-5145" data-mp="2b4d3irq7xarh">
                <button onClick={() => setShowIntakeModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5146" data-mp="2b6qsqjy7vsr4">
                  Cancel
                </button>
                <div className="flex gap-2" data-id="element-5147" data-mp="2yuqueex2h3kh">
                  <button onClick={() => {
                if (showIntakeModal) handleStatusChange(showIntakeModal, 'paused', intakeMessage);
                setShowIntakeModal(null);
              }} className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700" data-id="element-5148" data-mp="2u38uzvaozb1m">
                    Pause List
                  </button>
                  <button onClick={() => {
                if (showIntakeModal) handleStatusChange(showIntakeModal, 'closed', intakeMessage);
                setShowIntakeModal(null);
              }} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-id="element-5149" data-mp="3u3koavhdk7kx">
                    Close List
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5150" data-mp="um4cagknozrv">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5151" data-mp="zo1z1ks9mcpk">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-5152" data-mp="lzrvmbysss2q" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-5153" data-mp="hwvkf6bl7y8t">
              <div className="flex items-center gap-4 mb-4" data-id="element-5154" data-mp="vpl4h57k2t3a">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5155" data-mp="qr051804odfw">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-5156" data-mp="32gxwvste6pe" />
                </div>
                <div data-id="element-5157" data-mp="3v69r4r19szei">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-5158" data-mp="3v72nqz6im8ix">
                    Delete List
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-5159" data-mp="cxsdfgc1rhy7">
                    Are you sure? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-5160" data-mp="1odh2qecrwt26">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5161" data-mp="1jggubwsgbe7w">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-id="element-5162" data-mp="2sd5jrkcg7wop">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}